import React, {useEffect, useState} from 'react';
import {
  StyleSheet,
  View,
  Pressable,
  ScrollView,
  useWindowDimensions,
} from 'react-native';
import Box from '@/Atoms/Box';
import CCText from '@/Atoms/CCText';
import Spacer from '@/Atoms/Spacer';
import {FontSizes, vs} from '@/Utils/Dimensions';
import {MaterialCommunityIcons} from '@expo/vector-icons';
import useArenaStore from '@/Store/ArenaStore';
import DotLoader from '../DotLoader';
import PlayerAvatar from '@/Screens/Leaderboard/components/PlayerAvatar';
import RankBadge from '@/Screens/Leaderboard/components/RankBadge';
import {
  lbColors,
  lbRadius,
  lbShadow,
  MEDAL,
  avatarColorForUser,
} from '@/Screens/Leaderboard/leaderboardTheme';
import {initialsForName} from '@/Screens/Leaderboard/useLeaderboardData';
import useGetArenaPlayerGames, {
  ArenaPlayerGame,
} from '../hooks/useGetArenaPlayerGames';
import useGetArenaStandings from '../hooks/useGetArenaStandings';
import ArenaPlayerGames from './ArenaPlayerGames';
import ArenaSectionHeader from './ArenaSectionHeader';
import ArenaStickyRankRow from './ArenaStickyRankRow';
import Pagination from '@/Screens/Games/Pagination';

// ── ArenaLeaderboard ─────────────────────────────────────────────────────────
// The live standings, split out of ArenaLobby into its own tab so the "Arena"
// tab stays a lean personal-status surface. Reads ArenaStore.standings, renders
// the themed leaderboard rows (win-share bars, medal ranks, presence dots), and
// keeps the tap-a-row → that player's game history → replay behaviour that used
// to live inside ArenaLobby (its own rowGamesHook so nothing else contends).
//
// Paging (an arena can hold ~1000 players):
//   page 1  — the socket broadcast, live every ~2s. No fetch.
//   page >1 — GET /arenaStandings, fetched on navigation only (no poll: every
//             live update already arrives by push, a deep page is a lookup).
// Split on purpose: setStandings is a wholesale replace, so if page 3 were fed
// from the store a 2s broadcast would yank the reader back to the top. The
// browsed page lives in this component instead, and the socket can only ever
// repaint page 1.
//
// Layout: the ROWS scroll inside their own viewport-capped ScrollView; the
// header, your pinned rank card and the pagination stay visible without any
// page scrolling. Your card is ALWAYS pinned once you have a rank (even when
// your row is on the open page — the inner scroll can move it out of view);
// tapping it jumps the pager to your page.

const PAGE_SIZE = 20;

const FIRE_COLOR = '#FF6B00';
const PRESENCE_GREEN = '#2E7D32';
const PRESENCE_AMBER = '#E8A317';
const PRESENCE_GREY = '#9AA0A6';

const FireIcon: React.FC<{size?: number}> = ({size = 16}) => (
  <MaterialCommunityIcons name="fire" size={size} color={FIRE_COLOR} />
);

// Liveness marker after the player name (absent on older payloads → nothing).
const PresenceIndicator: React.FC<{presence?: string}> = ({presence}) => {
  if (presence === 'playing') {
    return (
      <View
        style={[styles.presenceDot, {backgroundColor: PRESENCE_GREEN}]}
        testID="presence-playing"
      />
    );
  }
  if (presence === 'waiting') {
    return (
      <View
        style={[styles.presenceDot, styles.presenceHollow]}
        testID="presence-waiting"
      />
    );
  }
  if (presence === 'paused') {
    return (
      <MaterialCommunityIcons
        name="pause"
        size={13}
        color={PRESENCE_GREY}
        testID="presence-paused"
      />
    );
  }
  if (presence === 'offline') {
    return (
      <View
        style={[styles.presenceDot, {backgroundColor: PRESENCE_GREY}]}
        testID="presence-offline"
      />
    );
  }
  return null;
};

const ArenaLeaderboard: React.FC<{
  loggedInUserId: string;
  tournamentId?: string | number;
  // A player mid-game must not spectate-jump (mirrors the ArenaLobby guard).
  playing?: boolean;
  // Opens a finished arena game's replay on the board (redis-first by tableId,
  // DB-uuid fallback) — threaded from PlayChess.openArenaGameByTable.
  openArenaGame?: (tableId: string, fallbackUuid?: string) => void;
}> = ({loggedInUserId, tournamentId, playing, openArenaGame}) => {
  const status = useArenaStore(state => state.status);
  const standings = useArenaStore(state => state.standings);

  // The rows scroll inside their own window so the sticky rank card and the
  // pagination never leave the viewport. Cap = window height minus the fixed
  // chrome above (nav / title / tab bars / section header) and the pinned
  // elements below; floored so short windows still show a usable list (there
  // the outer tab ScrollView takes over).
  const {height: winH} = useWindowDimensions();
  const listMaxHeight = Math.max(320, winH - 460);

  // Self-contained 1s ticker so "finishing" can flip from endsAtMs alone.
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  // Tap-a-standings-row → that player's full game history, inline.
  const rowGamesHook = useGetArenaPlayerGames();
  const [expandedUid, setExpandedUid] = useState<string | null>(null);

  const openGameOnBoard = (game: ArenaPlayerGame) => {
    if (playing) {
      return; // don't jump off your live board
    }
    openArenaGame?.(game.tableId, game.gameUuid);
  };

  const toggleRow = (uid: string) => {
    if (expandedUid === uid) {
      setExpandedUid(null);
      rowGamesHook.clear();
    } else {
      // clear the PREVIOUS row's list first, else switching A→B briefly shows
      // A's games under B until B's fetch resolves (shared hook).
      rowGamesHook.clear();
      setExpandedUid(uid);
      rowGamesHook.fetchPlayerGames(tournamentId, uid);
    }
  };

  const arenaState = status?.state || standings?.state || '';
  const endsAtMs = status?.endsAtMs || standings?.endsAtMs || 0;
  const finished = arenaState === 'finished';
  const finishing =
    arenaState === 'finishing' ||
    arenaState === 'finalizing' ||
    (!finished && endsAtMs > 0 && endsAtMs <= now);
  const notStarted = arenaState === 'created' || (!arenaState && !endsAtMs);
  const running = !finished && !finishing && !notStarted;

  const entries = standings?.entries || [];

  // ── paging ────────────────────────────────────────────────────────────────
  const [page, setPage] = useState(1);
  // A different arena must not inherit the previous one's page.
  useEffect(() => {
    setPage(1);
  }, [tournamentId]);

  const pageHook = useGetArenaStandings(
    tournamentId,
    page,
    PAGE_SIZE,
    loggedInUserId,
    page > 1, // page 1 comes from the socket — never duplicate that traffic
  );

  // Page-1 bootstrap: before the first standings broadcast lands, `entries`
  // is empty and the leaderboard would otherwise sit blank. Fetch page 1 over
  // the same REST surface deep pages use, but only while the store is still
  // empty — the moment a broadcast ships ANY entries, `entries` wins below and
  // this hook is disabled again, so a late-arriving REST response can never
  // clobber newer socket data (mirrors how a deep page's pageHook.data never
  // touches the store either).
  const page1Hook = useGetArenaStandings(
    tournamentId,
    1,
    PAGE_SIZE,
    loggedInUserId,
    page === 1 && entries.length === 0,
  );

  // ZCARD (live) / COUNT (archived) — the true roster size, not the number of
  // rows the broadcast happened to ship.
  const total =
    standings?.total ??
    pageHook.data?.total ??
    page1Hook.data?.total ??
    entries.length;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // Clamp if the roster shrank under a deep pager.
  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const pageEntries =
    page === 1
      ? entries.length > 0
        ? entries.slice(0, PAGE_SIZE)
        : page1Hook.data?.entries || []
      : pageHook.data?.entries || [];

  // The win-share bar is a fraction of the LEADER's points, so it must always
  // divide by rank 1 — not by the first row of whatever page is open, which on
  // page 3 would make every bar look full. The socket broadcast always carries
  // the leader, so prefer it and only fall back when it is absent.
  const leaderPoints = Math.max(
    1,
    Number(entries[0]?.points) || Number(pageEntries[0]?.points) || 1,
  );

  // ── your own row, always reachable ────────────────────────────────────────
  // rank/points/gamesPlayed ride arena_status_response, which is per-user (the
  // standings broadcast is per-room and cannot carry a per-caller row). A rank
  // of 0/absent means the caller never joined — a spectator gets no sticky row.
  const myRank = Number(status?.rank) || 0;
  // Pinned whenever the caller HAS a rank: with the rows scrolling in their own
  // window, "my row is on this page" no longer means "my row is on screen".
  // Spectators (no rank) still get nothing.
  const showStickyRow = myRank > 0 && pageEntries.length > 0;
  const jumpToMyRow = () => setPage(Math.ceil(myRank / PAGE_SIZE));

  // Presence summary ("3 playing · 2 waiting · 4 offline").
  const counts = standings?.counts;
  const presenceSummary = counts
    ? [
        counts.playing ? `${counts.playing} playing` : '',
        counts.waiting ? `${counts.waiting} waiting` : '',
        counts.online ? `${counts.online} online` : '',
        counts.offline ? `${counts.offline} offline` : '',
      ]
        .filter(Boolean)
        .join(' · ')
    : '';

  return (
    <Box style={styles.container}>
      <ArenaSectionHeader
        title={finished ? 'Final standings' : 'Leaderboard'}
        live={running}
        subtitle={!finished ? presenceSummary : undefined}
      />
      <Spacer spacing={vs(8)} />

      {pageEntries.length > 0 ? (
        <Box style={styles.listCard}>
          {/* Only the rows scroll; card chrome, sticky row and pager stay put. */}
          <ScrollView
            style={{maxHeight: listMaxHeight}}
            showsVerticalScrollIndicator={false}>
            {pageEntries.map(entry => {
              const uid = String(entry.uid);
              const isMe = uid === String(loggedInUserId);
              const dimmed = entry.presence === 'offline';
              const medal = MEDAL[entry.rank] || null;
              const barPct = Math.max(
                4,
                Math.round(((Number(entry.points) || 0) / leaderPoints) * 100),
              );
              const barColor = isMe
                ? lbColors.teal
                : medal
                ? medal.bg
                : lbColors.gold;
              const expanded = expandedUid === uid;
              return (
                <Box key={uid}>
                  <Pressable
                    onPress={() => toggleRow(uid)}
                    style={({pressed}: any) => [
                      styles.row,
                      isMe ? {backgroundColor: lbColors.youRowBg} : null,
                      pressed ? {backgroundColor: lbColors.rowBg} : null,
                      dimmed ? styles.dimmed : null,
                    ]}
                    data-testid="arena-standings-row">
                    <View style={styles.rankCell}>
                      <RankBadge rank={entry.rank} medal={medal} />
                    </View>
                    <PlayerAvatar
                      initials={initialsForName(entry.username)}
                      color={isMe ? lbColors.teal : avatarColorForUser(uid)}
                      size={40}
                    />
                    <Box style={styles.nameCell}>
                      <Box flexDirection="row" alignItems="center" gap={6}>
                        <CCText
                          fontSize={FontSizes[14]}
                          fontWeight={isMe ? 'bold' : 'semibold'}
                          numberOfLines={1}
                          style={{color: lbColors.ink, flexShrink: 1}}>
                          {entry.username}
                        </CCText>
                        {Boolean(entry.rating) && (
                          <CCText
                            fontSize={FontSizes[11]}
                            style={{color: lbColors.muted}}>
                            {`(${entry.rating})`}
                          </CCText>
                        )}
                        <PresenceIndicator presence={entry.presence} />
                        {entry.onFire && <FireIcon size={14} />}
                        {isMe && (
                          <View style={styles.youTag}>
                            <CCText
                              fontSize={FontSizes[10]}
                              fontWeight="bold"
                              color="#FFFFFF">
                              YOU
                            </CCText>
                          </View>
                        )}
                      </Box>
                      <View style={styles.barTrack}>
                        <View
                          style={[
                            styles.barFill,
                            {width: `${barPct}%`, backgroundColor: barColor},
                          ]}
                        />
                      </View>
                    </Box>
                    <Box style={styles.rightCell}>
                      <CCText
                        fontSize={FontSizes[18]}
                        fontWeight="bold"
                        style={{color: lbColors.ink}}>
                        {entry.points}
                      </CCText>
                      <CCText
                        fontSize={FontSizes[10]}
                        style={{color: lbColors.muted}}>
                        {entry.gamesPlayed} games
                      </CCText>
                    </Box>
                    <MaterialCommunityIcons
                      name={expanded ? 'chevron-up' : 'chevron-down'}
                      size={18}
                      color={lbColors.faint}
                    />
                  </Pressable>
                  {expanded && (
                    <ArenaPlayerGames
                      games={rowGamesHook.games}
                      loading={rowGamesHook.loading}
                      disabled={Boolean(playing)}
                      emptyText={`${entry.username} has no finished games yet.`}
                      onOpenGame={openGameOnBoard}
                    />
                  )}
                </Box>
              );
            })}
          </ScrollView>
        </Box>
      ) : (
        <Box style={styles.emptyState}>
          {(!finished || page > 1) && <DotLoader />}
          <Spacer spacing={vs(8)} />
          <CCText fontSize={FontSizes[13]} style={{color: lbColors.muted}}>
            {/* A deep page in flight is NOT an empty arena — saying "standings
                will appear once the first games finish" while fetching page 3
                of a full leaderboard would be plainly wrong. */}
            {page > 1
              ? 'Loading page…'
              : finished
              ? standings === null
                ? 'Loading final standings…'
                : 'No games were completed in this arena.'
              : standings === null && page1Hook.data === null
              ? 'Waiting for standings…'
              : 'No players have joined yet.'}
          </CCText>
        </Box>
      )}

      {/* Your row when it is not on the page being browsed. Tap to jump to it. */}
      {showStickyRow && (
        <ArenaStickyRankRow
          rank={myRank}
          points={Number(status?.points) || 0}
          gamesPlayed={Number(status?.gamesPlayed) || 0}
          total={total}
          rating={Number(status?.rating) || 0}
          onFire={Boolean(status?.onFire)}
          onPress={jumpToMyRow}
        />
      )}

      {totalPages > 1 && (
        <>
          <Spacer spacing={vs(8)} />
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            paginate={setPage}
            maxPage={7}
            activeColor
          />
          <CCText
            fontSize={FontSizes[11]}
            style={{color: lbColors.muted, textAlign: 'center'}}>
            {`${total} players`}
          </CCText>
        </>
      )}
    </Box>
  );
};

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: 8,
    paddingBottom: 16,
    backgroundColor: lbColors.bg,
  },
  listCard: {
    backgroundColor: lbColors.card,
    borderRadius: lbRadius.xl,
    paddingHorizontal: 8,
    paddingVertical: 6,
    ...lbShadow.card,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingHorizontal: 8,
    paddingVertical: 8,
    borderRadius: lbRadius.md,
    minHeight: 60,
  },
  rankCell: {
    width: 34,
    alignItems: 'center',
  },
  nameCell: {
    flex: 1,
    minWidth: 0,
  },
  youTag: {
    backgroundColor: lbColors.teal,
    paddingHorizontal: 7,
    paddingVertical: 1,
    borderRadius: lbRadius.pill,
  },
  barTrack: {
    height: 6,
    borderRadius: 3,
    backgroundColor: lbColors.barTrack,
    marginTop: 7,
    overflow: 'hidden',
  },
  barFill: {
    height: '100%',
    borderRadius: 3,
    minWidth: 6,
  },
  rightCell: {
    alignItems: 'flex-end',
    minWidth: 54,
  },
  presenceDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  presenceHollow: {
    backgroundColor: 'transparent',
    borderWidth: 1.5,
    borderColor: PRESENCE_AMBER,
  },
  dimmed: {
    opacity: 0.55,
  },
  emptyState: {
    alignItems: 'center',
    paddingVertical: 24,
  },
});

export default ArenaLeaderboard;
